import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Text,
  HStack,
  VStack,
  Spinner,
  Center,
  Badge,
  Tooltip,
  Button,
  ButtonGroup,
  useBreakpointValue,
} from "@chakra-ui/react";
import axios from "axios";
import proxy from "../../proxy";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Intensitatea celulei fata de cea mai mare luna din tabel.
const heatBg = (value, max) => {
  if (!value || max <= 0) return "transparent";
  const t = Math.min(value / max, 1);
  return `rgba(56, 178, 172, ${(0.08 + t * 0.42).toFixed(3)})`;
};

const DividendMatrix = () => {
  const [data, setData] = useState({ years: [], cells: [], firstDate: null, lastDate: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("net"); // "net" | "gross"

  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${proxy}server_mts/GetDividendMatrix.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (cancelled) return;
        setData(res.data || { years: [], cells: [] });
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load dividend history.");
      })
      .finally(() => !cancelled && setIsLoading(false));

    return () => { cancelled = true; };
  }, []);

  const { years, grid, yearTotals, monthTotals, maxCell, grandTotal, totalPayments } = useMemo(() => {
    const years = data.years || [];
    // grid[month][year] = { gross, tax, net, payments }
    const grid = {};
    const yearTotals = {};
    const monthTotals = {};
    let maxCell = 0;
    let grandTotal = 0;
    let totalPayments = 0;

    years.forEach((y) => { yearTotals[y] = 0; });

    (data.cells || []).forEach((c) => {
      if (!grid[c.month]) grid[c.month] = {};
      grid[c.month][c.year] = c;

      const v = mode === "net" ? c.net : c.gross;
      yearTotals[c.year]  = (yearTotals[c.year]  || 0) + v;
      monthTotals[c.month] = (monthTotals[c.month] || 0) + v;
      grandTotal    += v;
      totalPayments += c.payments;
      if (v > maxCell) maxCell = v;
    });

    return { years, grid, yearTotals, monthTotals, maxCell, grandTotal, totalPayments };
  }, [data, mode]);

  // Creșterea an-la-an, calculata doar pe lunile in care ambii ani au date —
  // altfel un an partial (ex. 2026 in curs) ar arata artificial ca o scadere.
  const yoy = useMemo(() => {
    const out = {};
    years.forEach((y, i) => {
      if (i === 0) return;
      const prev = years[i - 1];
      let cur = 0, ref = 0;
      for (let m = 1; m <= 12; m++) {
        const a = grid[m]?.[y];
        const b = grid[m]?.[prev];
        if (a && b) {
          cur += mode === "net" ? a.net : a.gross;
          ref += mode === "net" ? b.net : b.gross;
        }
      }
      out[y] = ref > 0 ? { pct: ((cur - ref) / ref) * 100, comparable: true } : { pct: null, comparable: false };
    });
    return out;
  }, [years, grid, mode]);

  if (isLoading) {
    return (
      <Center h="200px">
        <Spinner size="md" color="teal.300" thickness="3px" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="200px">
        <VStack spacing={1}>
          <Text fontSize="2xl">📉</Text>
          <Text fontSize="sm" color="red.300">{error}</Text>
        </VStack>
      </Center>
    );
  }

  if (years.length === 0) {
    return (
      <Center h="200px">
        <VStack spacing={1}>
          <Text fontSize="2xl">📊</Text>
          <Text fontSize="sm" color="gray.400">No dividends recorded yet.</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box
      p={{ base: 3, md: 4 }}
      borderWidth="1px"
      borderColor="gray.700"
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      bg="gray.800"
    >
      {/* Header */}
      <HStack
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        flexDirection={{ base: "column", md: "row" }}
        spacing={3}
        mb={4}
      >
        <VStack spacing={0} align="flex-start">
          <Text fontSize={{ base: "xs", md: "sm" }} color="teal.300" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
            📊 Dividend History
          </Text>
          <Text fontSize="xs" color="gray.500">
            {data.firstDate ? `Since ${data.firstDate}` : "Monthly income"} · {totalPayments} payments
          </Text>
        </VStack>

        <ButtonGroup size="xs" isAttached variant="outline">
          {[
            { key: "net", label: "Net" },
            { key: "gross", label: "Gross" },
          ].map((m) => (
            <Button
              key={m.key}
              onClick={() => setMode(m.key)}
              bg={mode === m.key ? "teal.500" : "transparent"}
              color={mode === m.key ? "white" : "teal.200"}
              borderColor="teal.600"
              _hover={{ bg: mode === m.key ? "teal.500" : "whiteAlpha.100" }}
              fontWeight="bold"
            >
              {m.label}
            </Button>
          ))}
        </ButtonGroup>
      </HStack>

      {/* Matrice luna x an */}
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.900">
            <Tr>
              <Th color="teal.200" fontSize="xs" textAlign="left" px={2} py={3}>
                Month
              </Th>
              {years.map((y) => (
                <Th key={y} color="teal.200" fontSize="xs" textAlign="center" px={2}>
                  {y}
                </Th>
              ))}
              {!isMobile && (
                <Th color="gray.400" fontSize="xs" textAlign="center" px={2}>
                  Total
                </Th>
              )}
            </Tr>
          </Thead>

          <Tbody>
            {MONTHS.map((label, idx) => {
              const month = idx + 1;
              return (
                <Tr key={month} borderBottom="1px solid rgba(255,255,255,0.06)">
                  <Td px={2} py={1.5} fontSize="xs" color="gray.400" fontWeight="semibold">
                    {label}
                  </Td>

                  {years.map((y) => {
                    const cell = grid[month]?.[y];
                    const value = cell ? (mode === "net" ? cell.net : cell.gross) : 0;

                    if (!cell) {
                      return (
                        <Td key={y} px={2} py={1.5} textAlign="center" color="gray.700" fontSize="xs">
                          —
                        </Td>
                      );
                    }

                    return (
                      <Td key={y} px={2} py={1.5} textAlign="center" bg={heatBg(value, maxCell)}>
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={`${label} ${y} — gross ${fmtMoney(cell.gross)}, tax ${fmtMoney(cell.tax)}, net ${fmtMoney(cell.net)} · ${cell.payments} payment${cell.payments !== 1 ? "s" : ""}`}
                        >
                          <Text fontSize="xs" fontWeight="semibold" color="white">
                            {fmtMoney(value)}
                          </Text>
                        </Tooltip>
                      </Td>
                    );
                  })}

                  {!isMobile && (
                    <Td px={2} py={1.5} textAlign="center" fontSize="xs" color="gray.400">
                      {monthTotals[month] ? fmtMoney(monthTotals[month]) : "—"}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>

          <Tfoot bg="gray.900">
            {/* Total pe an */}
            <Tr>
              <Td px={2} py={2} fontSize="xs" fontWeight="bold" color="teal.200">
                TOTAL
              </Td>
              {years.map((y) => (
                <Td key={y} px={2} py={2} textAlign="center">
                  <Text fontSize="sm" fontWeight="bold" color="green.300">
                    {fmtMoney(yearTotals[y] || 0)}
                  </Text>
                </Td>
              ))}
              {!isMobile && (
                <Td px={2} py={2} textAlign="center">
                  <Text fontSize="sm" fontWeight="black" color="green.200">
                    {fmtMoney(grandTotal)}
                  </Text>
                </Td>
              )}
            </Tr>

            {/* Creștere an-la-an, pe lunile comparabile */}
            {years.length > 1 && (
              <Tr>
                <Td px={2} py={2} fontSize="2xs" color="gray.500" textTransform="uppercase">
                  <Tooltip
                    hasArrow
                    placement="top"
                    label="Year-over-year growth, computed only on the months where both years have data — so a year still in progress isn't shown as a drop."
                  >
                    <Text>YoY</Text>
                  </Tooltip>
                </Td>
                {years.map((y, i) => {
                  if (i === 0) {
                    return (
                      <Td key={y} px={2} py={2} textAlign="center" color="gray.700" fontSize="xs">
                        —
                      </Td>
                    );
                  }
                  const g = yoy[y];
                  if (!g || !g.comparable) {
                    return (
                      <Td key={y} px={2} py={2} textAlign="center" color="gray.600" fontSize="xs">
                        n/a
                      </Td>
                    );
                  }
                  const up = g.pct >= 0;
                  return (
                    <Td key={y} px={2} py={2} textAlign="center">
                      <Badge
                        colorScheme={up ? "green" : "red"}
                        fontSize="2xs"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                      >
                        {up ? "▲" : "▼"} {Math.abs(g.pct).toFixed(1)}%
                      </Badge>
                    </Td>
                  );
                })}
                {!isMobile && <Td />}
              </Tr>
            )}
          </Tfoot>
        </Table>
      </Box>

      <Text fontSize="2xs" color="gray.600" mt={3} textAlign="center">
        {mode === "net" ? "Net of withholding tax" : "Before withholding tax"} · darker cells = higher income
      </Text>
    </Box>
  );
};

export default DividendMatrix;
